import { useCallback } from "react"
import { useSearchParams } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import type { AxiosResponse } from "axios"
import { useDrawing, useUpdateDrawing } from "hooks/api/drawings/drawings.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import type { TDrawingProjectNumber } from "types/drawer.type"

/**
 * Single source of truth for a drawing's project number (REQ-379 / REQ-377).
 *
 * Reads the current value from the cached drawing (`useDrawing`, which returns it as `projectNumber`)
 * and exposes a setter shared by the inline header editor and the create-quotation gate, so both always
 * agree. `setProjectNumber` persists via the drawings PATCH endpoint, updating the React Query cache
 * optimistically and reconciling with the server response (or rolling back on failure). It resolves to
 * `true` on success and `false` on failure so callers (e.g. the gate) can decide whether to proceed.
 */
export const useProjectNumber = (drawingIdArg?: number) => {
	const [searchParams] = useSearchParams()
	const drawingId = drawingIdArg || Number(searchParams.get("drawingID")) || 0
	const queryClient = useQueryClient()
	const { mutateAsync } = useUpdateDrawing()
	const { formatError } = useApiErrorFormatter()

	const { data: drawing, isPending } = useDrawing(drawingId || null)
	const projectNumber = (drawing as TDrawingProjectNumber | undefined)?.projectNumber ?? ""

	const queryKey = ["drawingById", drawingId]

	// `useDrawing` caches the raw axios response and `select`s `.data`, so cache writes touch `.data`.
	const writeCache = useCallback(
		(value: string | null) => {
			queryClient.setQueryData<AxiosResponse<TDrawingProjectNumber> | undefined>(queryKey, (cached) =>
				cached ? { ...cached, data: { ...cached.data, projectNumber: value } } : cached,
			)
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[queryClient, drawingId],
	)

	const setProjectNumber = useCallback(
		async (value: string): Promise<boolean> => {
			if (!drawingId) return false
			const trimmed = value.trim()
			const normalized = trimmed.length > 0 ? trimmed : null
			if (normalized === (projectNumber || null)) return true

			// Optimistically update so the header editor and the create-quotation gate reflect the change
			// immediately; keep the prior response to roll back to if the PATCH fails.
			const previous = queryClient.getQueryData<AxiosResponse<TDrawingProjectNumber>>(queryKey)
			writeCache(normalized)

			try {
				await mutateAsync({ id: drawingId, projectNumber: normalized })
				// The optimistic value is already in the cache. Rather than trust the PATCH response
				// body's shape, invalidate so the drawing reconciles from its canonical GET — this keeps
				// every field fresh and honestly reflects the server (a silent no-op would revert the
				// value here instead of being masked). Not awaited: the refetch runs in the background.
				queryClient.invalidateQueries({ queryKey })
				return true
			} catch (error) {
				queryClient.setQueryData(queryKey, previous)
				formatError(error)
				return false
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[drawingId, projectNumber, queryClient, mutateAsync, writeCache, formatError],
	)

	return {
		drawingId,
		projectNumber,
		isSet: projectNumber.trim().length > 0,
		isLoading: !!drawingId && isPending,
		setProjectNumber,
	}
}
